<template>
<div>
    <law-matter-evidence-form :getLawMatterEvidenceList="getLawMatterEvidenceList" ref="lawMatterEvidenceFormModal" :lawMatterId="lawMatterId"></law-matter-evidence-form>
    <b-row>
        <b-col class="col-12">
            <div style="float:left;" class="custom-search d-flex justify-content-end">
                <b-form-group>
                    <div class="d-flex align-items-center">
                        <b-form-input size="sm" v-model="searchTerm" :placeholder="$t('others.search')" type="text" class="d-inline-block" />
                    </div>
                </b-form-group>
            </div>
            <div style="float:right;" class="custom-search d-flex justify-content-end">
                <b-form-group>
                    <div class="d-flex align-items-center">
                        <b-button style="float:right; border-color:#6B7280; color:#374151;" size="sm" variant="outline-secondary" @click="getInfo(0)">{{ $t('lawMatterEvidence.add') }}</b-button>
                    </div>
                </b-form-group>
            </div>
        </b-col>
    </b-row>
    <!-- table -->
    <vue-good-table id="lawMatterEvidenceTable" ref="lawMatterEvidenceTable" :columns="columns" :rows="rows" styleClass="vgt-table condensed tableSize" :search-options="{
        enabled: true,
        externalQuery: searchTerm
      }" :select-options="{
        enabled: false,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
        selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
      }" :pagination-options="{
        enabled: true,
        perPage: pageLength
      }" theme="my-theme">
        <div slot="emptystate">
            {{ $t('dataTable.data_not_found') }}
        </div>
        <!-- Row: Table Row -->
        <template slot="table-column" slot-scope="props">
            <span v-if="props.column.field === 'isOriginalProduct'">
                {{ $t('lawMatterEvidence.is_original_product') }}
            </span>
            <span v-else-if="props.column.field === 'isImitationProduct'">
                {{ $t('lawMatterEvidence.is_imitation_product') }}
            </span>
            <span v-else-if="props.column.field === 'purchaseDate'" class="text-nowrap">
                {{ $t('lawMatterEvidence.purchase_date') }}
            </span>
            <span v-else-if="props.column.field === 'arrivalDateFromClient'">
                {{ $t('lawMatterEvidence.arrival_date_from_client') }}
            </span>
            <span v-else-if="props.column.field === 'arrivalDateFromCerberus'">
                {{ $t('lawMatterEvidence.arrival_date_from_cerberus') }}
            </span>
            <span v-else-if="props.column.field === 'note'" class="text-nowrap">
                {{ $t('lawMatterEvidence.note') }}
            </span>
            <span v-else-if="props.column.field === 'process'" class="text-nowrap"></span>
        </template>
        <!-- Column: Table Column -->
        <template slot="table-row" slot-scope="props">
            <!-- Column: Process -->
            <span v-if="props.column.field === 'process'">
                <list-actions-dropdown :actions="[
                    { icon: 'EditIcon', label: $t('others.edit'), handler: () => getInfo(props.row.id) },
                    { icon: 'XIcon', label: $t('others.delete'), color: '#DC2626', handler: () => deleted(props.row) }
                ]" />
            </span>
            <span v-else-if="props.column.field === 'isOriginalProduct'">
                <b-form-checkbox disabled v-model="props.row.isOriginalProduct" />
            </span>
            <span v-else-if="props.column.field === 'isImitationProduct'">
                <b-form-checkbox disabled v-model="props.row.isImitationProduct" />
            </span>
            <span v-else-if="props.column.field === 'note'" v-b-tooltip.hover="{ title: props.row.note, customClass: 'wh-tooltip' }" class="wh-clamp-2">
                {{ props.row.note }}
            </span>
            <span v-else-if="props.column.field === 'images'" class="text-nowrap">
                <b-row v-if="props.row.images !== null">
                     <span v-for="(image, index) in props.row.images.filter(p => p.fileName)" :key="index">
                        <b-img :src="'data:image/png;base64,' + image.base64" fluid alt="Img" style="width: 8em; height: 8em;" />
                    </span> 
                </b-row>
            </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-0 mt-1">
                    <span class="text-nowrap"> {{ $t('dataTable.total') }} {{ props.total }} {{ $t('dataTable.record') }}
                    </span>
                    <b-form-select v-model="pageLength" :options="['10', '25', '50', '100']" class="mx-1" @input="(value) => props.perPageChanged({ currentPerPage: value })" />
                    <span class="text-nowrap"> {{ $t('dataTable.row_is_show') }} </span>
                </div>
                <div>
                    <b-pagination :value="1" :total-rows="props.total" :per-page="pageLength" first-number last-number align="right" prev-class="prev-item" next-class="next-item" class="mt-1 mb-0" @input="(value) => props.pageChanged({ currentPage: value })">
                        <template #prev-text>
                            <feather-icon icon="ChevronLeftIcon" size="18" />
                        </template>
                        <template #next-text>
                            <feather-icon icon="ChevronRightIcon" size="18" />
                        </template>
                    </b-pagination>
                </div>
            </div>
        </template>
    </vue-good-table>
</div>
</template>

<script>
export default {
    props: {
        getLawMatterList: {
            type: Function
        },
        lawMatterId: {
            type: Number
        }
    },
    data() {
        return {
            pageLength: 50,
            dir: false,
            columns: [{
                    label: this.$t('lawMatterEvidence.is_original_product'),
                    field: `isOriginalProduct`,
                },
                {
                    label: this.$t('lawMatterEvidence.is_imitation_product'),
                    field: `isImitationProduct`,
                },
                {
                    label: this.$t('lawMatterEvidence.purchase_date'),
                    field: `purchaseDate`,
                    formatFn: this.$formatFn,
                },
                {
                    label: this.$t('lawMatterEvidence.arrival_date_from_client'),
                    field: `arrivalDateFromClient`,
                    formatFn: this.$formatFn,
                },
                {
                    label: this.$t('lawMatterEvidence.arrival_date_from_cerberus'),
                    field: `arrivalDateFromCerberus`,
                    formatFn: this.$formatFn,
                },
                {
                    label: this.$t('lawMatterEvidence.note'),
                    field: `note`,
                },
                {
                    label: this.$t('lawMatterEvidence.image'),
                    field: `images`,
                },
                {
                    label: '',
                    field: 'process',
                    width: '10em',
                    sortable: false
                },
            ],
            rows: [],
            searchTerm: '',
        }
    },
    mounted() {
        this.getLawMatterEvidenceList()
    },
    methods: {
        async deleted(data) {
            data = {
                id: data.id
            }
            const controller = {
                name: 'LawMatterEvidence'
            }
            const payload = {
                data: data,
                controller: controller
            }
            this.$DeleteConfirm().then(result => {
                if (result.value) {
                    this.$store.dispatch('moduleAuth/delete', payload).then((response) => {
                        if (response.data.resultStatus == true) {
                            this.$DeleteAlert()
                            this.getLawMatterEvidenceList()
                        } else {
                            this.$WarningAlert(response.data.resultMessage)
                            this.getLawMatterEvidenceList()
                        }
                    })
                }
            })
        },
        async getInfo(id) {
            this.$refs.lawMatterEvidenceFormModal.showInfo(id, this.lawMatterId)
        },
        getLawMatterEvidenceList() {
            this.rows = []
            const data = {
                id: this.lawMatterId
            }
            const controller = {
                name: 'LawMatterEvidence',
                actionName: 'GetLawMatterEvidenceListByLawMatterId'
            }
            const payload = {
                controller: controller,
                data: data
            }
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                if (response.data.resultStatus == true) {
                    response.data.lawMatterEvidenceList.forEach(element => {
                        this.rows.push(element)
                    })
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
            })
        },
    },
}
</script>

<style lang="scss">
.tableSize {
    font-size: 13px !important;
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    table-layout: auto;
}
</style>
